import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, Bike, ShoppingBag } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useCart, gbp } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

function buildSlots(hours) {
  const days = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
  const now = new Date();
  const cfg = hours?.[days[(now.getDay()+6)%7]];
  if (!cfg || cfg.closed) return [];
  const [ch, cm] = cfg.close.split(":").map(Number);
  const close = new Date(now); close.setHours(ch, cm, 0, 0);
  let t = new Date(now.getTime() + 30*60000);
  t.setMinutes(Math.ceil(t.getMinutes()/15)*15, 0, 0);
  const slots = [];
  while (t <= close && slots.length < 20) {
    slots.push(t.toTimeString().slice(0,5));
    t = new Date(t.getTime() + 15*60000);
  }
  return slots;
}

export default function Checkout() {
  const navigate = useNavigate();
  const { items, mode, subtotal, count } = useCart();
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [addr, setAddr] = useState({ line1: "", line2: "", town: "", postcode: "" });
  const [instructions, setInstructions] = useState("");
  const [timeType, setTimeType] = useState("asap");
  const [slot, setSlot] = useState("");
  const [payUrl, setPayUrl] = useState("");

  useEffect(() => {
    if (count === 0) { navigate("/menu"); return; }
    api.get("/settings/public").then((r) => setSettings(r.data));
  }, []);

  const slots = settings ? buildSlots(settings.opening_hours) : [];
  let deliveryCharge = 0;
  if (mode === "delivery" && settings) {
    deliveryCharge = Number(settings.delivery_charge || 0);
    const freeTh = Number(settings.free_delivery_threshold || 0);
    if (freeTh && subtotal >= freeTh) deliveryCharge = 0;
  }
  const serviceCharge = Number(settings?.service_charge || 0);
  const total = subtotal + deliveryCharge + serviceCharge;
  const estimate = mode === "delivery" ? settings?.delivery_estimate : settings?.collection_estimate;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setA = (k, v) => setAddr((a) => ({ ...a, [k]: v }));

  const valid = () => {
    if (!form.first_name || !form.last_name || !form.phone || !form.email) return "Please complete your details";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email";
    if (mode === "delivery" && (!addr.line1 || !addr.town || !addr.postcode)) return "Please complete your delivery address";
    if (timeType === "scheduled" && !slot) return "Please choose a time slot";
    return null;
  };

  const pay = async () => {
    const err = valid();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        type: mode,
        customer: form,
        address: mode === "delivery" ? addr : null,
        delivery_instructions: instructions,
        order_time_type: timeType,
        scheduled_time: timeType === "scheduled" ? slot : "",
        items: items.map((l) => ({ item_id: l.item_id, qty: l.qty, extras: l.extras.map((e) => ({ id: e.id, name: e.name, price: e.price })), notes: l.notes })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout", payload);
      const url = data.checkout_url;
      // Top-level tab (phone / direct link): redirect straight away.
      if (window.self === window.top) {
        window.location.href = url;
        return;
      }
      // Inside an iframe (e.g. preview pane): Stripe cannot render framed, and
      // Safari blocks auto break-out after an async call. So surface a real link
      // the user taps — a genuine click opens Stripe in the top window reliably.
      setPayUrl(url);
      setSubmitting(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream grain pb-32">
      <div className="sticky top-0 z-30 bg-brand-dark text-brand-cream">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="back-menu" onClick={() => navigate("/menu")} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-head text-xl font-bold">Checkout</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-5 space-y-5">
        <div className="rounded-2xl bg-brand-teal/10 border border-brand-teal/30 p-4 flex items-center gap-3">
          {mode === "delivery" ? <Bike className="w-5 h-5 text-brand-teal" /> : <ShoppingBag className="w-5 h-5 text-brand-teal" />}
          <div className="flex-1">
            <p className="font-semibold text-brand-teal-dark capitalize">{mode}</p>
            <p className="text-sm text-brand-dark/60 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Approximately {estimate}</p>
          </div>
        </div>

        <section className="bg-white rounded-2xl p-4 border border-brand-dark/5">
          <h2 className="font-head font-bold text-lg mb-3">Your details</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First name</Label><Input data-testid="fname" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Last name</Label><Input data-testid="lname" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Mobile number</Label><Input data-testid="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Email</Label><Input data-testid="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 rounded-xl" /></div>
          </div>
        </section>

        {mode === "delivery" && (
          <section className="bg-white rounded-2xl p-4 border border-brand-dark/5">
            <h2 className="font-head font-bold text-lg mb-3">Delivery address</h2>
            <div className="space-y-3">
              <div><Label className="text-xs">Address line 1</Label><Input data-testid="addr1" value={addr.line1} onChange={(e) => setA("line1", e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Address line 2 (optional)</Label><Input data-testid="addr2" value={addr.line2} onChange={(e) => setA("line2", e.target.value)} className="mt-1 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Town</Label><Input data-testid="town" value={addr.town} onChange={(e) => setA("town", e.target.value)} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-xs">Postcode</Label><Input data-testid="dpostcode" value={addr.postcode} onChange={(e) => setA("postcode", e.target.value)} className="mt-1 rounded-xl uppercase" /></div>
              </div>
              <div><Label className="text-xs">Delivery instructions (optional)</Label><Textarea data-testid="instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Blue door — please knock." className="mt-1 rounded-xl resize-none" rows={2} /></div>
            </div>
          </section>
        )}

        <section className="bg-white rounded-2xl p-4 border border-brand-dark/5">
          <h2 className="font-head font-bold text-lg mb-3">When?</h2>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="time-asap" onClick={() => setTimeType("asap")}
              className={`py-3 rounded-xl font-semibold text-sm border ${timeType === "asap" ? "bg-brand-teal text-white border-brand-teal" : "border-brand-dark/15 text-brand-dark/60"}`}>ASAP</button>
            <button data-testid="time-later" onClick={() => setTimeType("scheduled")}
              className={`py-3 rounded-xl font-semibold text-sm border ${timeType === "scheduled" ? "bg-brand-teal text-white border-brand-teal" : "border-brand-dark/15 text-brand-dark/60"}`}>Schedule for later</button>
          </div>
          {timeType === "scheduled" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {slots.length === 0 && <p className="text-sm text-brand-dark/50">No slots available today.</p>}
              {slots.map((s) => (
                <button key={s} data-testid={`slot-${s}`} onClick={() => setSlot(s)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${slot === s ? "bg-brand-dark text-white border-brand-dark" : "border-brand-dark/15"}`}>{s}</button>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl p-4 border border-brand-dark/5">
          <h2 className="font-head font-bold text-lg mb-3">Order summary</h2>
          {items.map((l) => (
            <div key={l.lineId} className="flex justify-between text-sm py-1">
              <span className="text-brand-dark/70">{l.qty}× {l.name}</span>
              <span className="font-medium">{gbp(l.unit_price * l.qty)}</span>
            </div>
          ))}
          <div className="border-t mt-2 pt-2 space-y-1">
            <div className="flex justify-between text-sm text-brand-dark/60"><span>Subtotal</span><span>{gbp(subtotal)}</span></div>
            {mode === "delivery" && <div className="flex justify-between text-sm text-brand-dark/60"><span>Delivery</span><span>{deliveryCharge === 0 ? "Free" : gbp(deliveryCharge)}</span></div>}
            {serviceCharge > 0 && <div className="flex justify-between text-sm text-brand-dark/60"><span>Online Ordering Fee</span><span>{gbp(serviceCharge)}</span></div>}
            <div className="flex justify-between font-bold text-lg text-brand-dark pt-1"><span>Total</span><span>{gbp(total)}</span></div>
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-brand-cream/80 backdrop-blur-md" style={{ boxShadow: "0 -4px 20px rgba(0,0,0,0.06)" }}>
        <div className="max-w-lg mx-auto">
          {payUrl ? (
            <>
              <a href={payUrl} target="_top" rel="noopener noreferrer" data-testid="continue-payment-btn"
                className="flex w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white text-base font-bold items-center justify-center active:scale-[0.98] transition-transform">
                Continue to secure payment →
              </a>
              <p className="text-center text-[11px] text-brand-dark/50 mt-2">Opens Stripe's secure checkout in this window.</p>
            </>
          ) : (
            <>
              <Button data-testid="pay-btn" onClick={pay} disabled={submitting}
                className="w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white text-base font-bold active:scale-[0.98] transition-transform">
                {submitting ? "Preparing secure payment…" : `Pay ${gbp(total)}`}
              </Button>
              <p className="text-center text-[11px] text-brand-dark/40 mt-2">Secure payment by Stripe · Card, Apple Pay & Google Pay</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
